import { useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { isEqual } from 'lodash';

import { NAVIGATION_AWAY_WARNING } from '@funnels/constants';
import { useAnalytics } from '@funnels/providers';
import { ReduxStoreData } from '@funnels/types';

import { NavigationBlockerModal } from './NavigationBlockerModal';

function mapState(currentState: ReduxStoreData) {
  const loading =
    currentState.applications.loading +
    currentState.applicants.loading +
    currentState.assets.loading +
    currentState.brokerInterviews.loading +
    currentState.brokerInterviewsGuarantor.loading +
    currentState.companies.loading +
    currentState.conveyancing.loading +
    currentState.creditReports.loading +
    currentState.customers.loading +
    currentState.employments.loading +
    currentState.expenses.loading +
    currentState.incomes.loading +
    currentState.invitations.loading +
    currentState.lead.loading +
    currentState.liabilities.loading +
    currentState.productPreferences.loading +
    currentState.postProductSelect.loading +
    currentState.productSelections.loading +
    currentState.relatedParties.loading +
    currentState.requiredActionConditions.loading;

  const locked = currentState.applications.locked;
  return { loading, locked };
}

interface NavigationBlockerProps {
  page: string;
  message?: string;
  delegateNavigation?: (path: string) => void;
  skip?: boolean;
  showDiscardButton?: boolean;
}

export default function NavigationBlocker({
  page,
  message = NAVIGATION_AWAY_WARNING,
  delegateNavigation,
  skip = false,
  showDiscardButton = true,
}: NavigationBlockerProps) {
  const { tracking } = useAnalytics();
  const history = useHistory();
  const form = useForm();
  const { valid, values, initialValues } = form.getState();
  const dirty = !isEqual(values, initialValues);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [nextLocation, setNextLocation] = useState<ReturnType<typeof useLocation>>();
  const { locked: applicationLockIsActive, loading } = useSelector(mapState);

  useEffect(() => {
    function handleBeforeUnload() {
      if (dirty && !skip) {
        return message;
      }
      return;
    }
    window.onbeforeunload = handleBeforeUnload;

    return () => {
      window.onbeforeunload = null;
    };
  }, [dirty, message, skip]);

  useEffect(() => {
    if (shouldNavigate && !!nextLocation && !loading) {
      setIsModalVisible(false);
      setShouldNavigate(false);
      history.push(nextLocation.pathname);
    }
  }, [shouldNavigate, nextLocation, history, loading]);

  if (dirty && !skip && !applicationLockIsActive) {
    return (
      <>
        <Prompt
          when={dirty}
          message={(location) => {
            if (shouldNavigate) return true;
            setIsModalVisible(true);
            tracking?.modal.displayed({
              modalName: valid ? 'Unsaved Changes' : 'Attention Required',
              modalType: 'Confirmation',
              origin: page,
            });
            setNextLocation(location);
            return false;
          }}
        />
        <NavigationBlockerModal
          isVisible={isModalVisible}
          showDiscardButton={showDiscardButton}
          onHide={() => {
            setIsModalVisible(false);
            tracking?.modal.closed({
              modalName: valid ? 'Unsaved Changes' : 'Attention Required',
              modalType: 'Confirmation',
              origin: page,
            });
          }}
          hasErrors={!valid}
          onReviewErrors={() => {
            setIsModalVisible(false);
            form.submit();
            tracking?.button.primary({ text: 'Review Errors' });
          }}
          onSaveChanges={() => {
            form.submit();
            if (!!delegateNavigation && !!nextLocation) {
              setIsModalVisible(false);
              delegateNavigation(nextLocation.pathname);
            } else {
              setShouldNavigate(true);
            }
            tracking?.button.primary({ text: 'Save Changes' });
          }}
          onDiscardChanges={() => {
            form.restart();
            setShouldNavigate(true);
            tracking?.button.secondary({ text: 'Discard Changes' });
          }}
        />
      </>
    );
  } else {
    return null;
  }
}
